import React from 'react';
import { Box } from '@mui/material';
import { getLogTypeByCategory } from '../../../constants/logTypeRegistry';
import {
  getCareLogTone,
  getCareLogCategoryMeta,
  DEFAULT_CATEGORY_COLORS,
} from '../../UI/careLogCardTheme';
import { resolveImportPreviewTiming } from './importLogsParsing';
import { getTimeBucketLabel, getTimeUnclearLabel } from '../../../utils/timeBucketLabel';
import { buildConciseBehaviorTitle } from '../../../utils/behaviorTitles';
import {
  CARE_MEDICATION_DOSE_ANY_UNIT_PATTERN,
  CARE_MEDICATION_REVIEW_NAME_PATTERN,
} from '../../../shared/careMedicationPatterns';
import { CARE_DATE_MONTH_NAME_PATTERN } from '../../../shared/careDateTimePatterns';

const isApproximateTimeSource = (timeSource = '') => timeSource === 'inferred' || timeSource === 'approximate';
const getTimeInferenceLabel = (timeBucket = '', timeInferenceReason = '') => {
  const reason = String(timeInferenceReason || timeBucket || '').trim().toLowerCase();
  if (!reason) {
    return '';
  }

  return getTimeBucketLabel(reason) || '';
};

export const CATEGORY_OPTIONS = [
  { value: 'behavior', label: 'Behavior' },
  { value: 'health', label: 'Health' },
  { value: 'medication', label: 'Medication' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'note', label: 'Note' },
  { value: 'sleep', label: 'Sleep' },
  { value: 'food', label: 'Food' },
  { value: 'activity', label: 'Activity' },
  { value: 'log', label: 'Daily Log' },
  { value: 'other', label: 'Other' },
];

export const IMPORTANCE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'important', label: 'Important' },
];

export const BEHAVIOR_SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'high', label: 'High' },
  { value: 'crisis', label: 'Crisis' },
];

export { DEFAULT_CATEGORY_COLORS };
export { getCareLogCategoryMeta };

export const getCategoryTone = getCareLogTone;

const titleCase = (value = '') => String(value || '')
  .trim()
  .replace(/\s+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getPreviewBodyText = (entry = {}) => String(
  entry.body
  || entry.note
  || entry.text
  || entry.content
  || entry.description
  || entry.summary
  || entry.value
  || entry.sourceText
  || entry.originalText
  || ''
).trim();

const getFirstSentence = (value = '') => String(value || '').trim().split(/(?<=[.!?])\s+/)[0]?.trim() || '';

const escapeRegExp = (value = '') => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const DATE_HEADING_PREFIX_PATTERN = new RegExp(
  `^(?:`
  + `${CARE_DATE_MONTH_NAME_PATTERN}\\s+\\d{4}`
  + `|`
  + `${CARE_DATE_MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:,)?\\s+\\d{4}`
  + `|`
  + `\\d{4}-\\d{2}-\\d{2}`
  + `|`
  + `\\d{1,2}/\\d{1,2}/\\d{4}`
  + `)`,
  'i'
);

const normalizeDateContextLabel = (value = '') => String(value || '').trim().replace(/\s+/g, ' ');

const stripLeadingDateContext = (text = '', entry = {}) => {
  const source = String(text || '').trim();
  if (!source) {
    return '';
  }

  const labels = [
    entry?.metadata?.dateLabel,
    entry?.metadata?.dateHeading,
    entry?.dateLabel,
    entry?.dateHeading,
  ]
    .map(normalizeDateContextLabel)
    .filter(Boolean);

  const tryStrip = (candidate = '') => {
    if (!candidate) return source;
    const escaped = escapeRegExp(candidate);
    const stripped = source
      .replace(new RegExp(`^${escaped}(?=\\s|$|[A-Z0-9])`, 'i'), '')
      .replace(/^[\s,;:.\-–—•·]+/, '')
      .trim();
    return stripped || source;
  };

  for (const candidate of labels) {
    const stripped = tryStrip(candidate);
    if (stripped !== source) {
      return stripped;
    }
  }

  const genericStripped = source
    .replace(DATE_HEADING_PREFIX_PATTERN, '')
    .replace(/^[\s,;:.\-–—•·]+/, '')
    .trim();

  return genericStripped || source;
};

const extractBodyDrivenTitle = (body = '') => {
  const comparable = getFirstSentence(body);
  if (!comparable) {
    return '';
  }

  return comparable
    .replace(/^(?:for\s+\w+,\s*)?(?:she|he|they|the child|child|student|maya|ethan|it|there)?\s*/i, '')
    .replace(/^(?:was|were|is|are|had|have|has|ate|drank|skipped|finished|refused|got|became|seemed|felt|gave|took|participated|played|laughed|cried|went|came|did|woke(?: up)?|fell asleep|slept)\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const isGenericTitle = (title = '', category = '') => {
  const normalizedTitle = String(title || '').trim().toLowerCase();
  const normalizedCategory = String(category || '').trim().toLowerCase();
  const normalizedCategoryLabel = getCategoryLabel(category).toLowerCase();
  const genericTitles = new Set([
    'note',
    'daily log',
    'behavior note',
    'food note',
    'mood note',
    'activity note',
    'sleep note',
    'entry',
    'update',
  ]);

  return !normalizedTitle
    || normalizedTitle === normalizedCategory
    || normalizedTitle === normalizedCategoryLabel
    || genericTitles.has(normalizedTitle)
    || (
      normalizedCategoryLabel
      && new RegExp(`^${escapeRegExp(normalizedCategoryLabel)}\\s*(?:—|-|–)\\s*(?:some|unknown|note|entry)?$`, 'i').test(normalizedTitle)
    );
};

const isVerboseSentenceTitle = (value = '') => {
  const text = String(value || '').trim();
  if (!text) {
    return false;
  }

  return (
    text.length > 42
    || /\b(?:but|because|while|when|then|after)\b/i.test(text)
    || /[,.!?;:]/.test(text)
  );
};

const resolveBehaviorPreviewTitle = (body = '') => {
  const comparable = String(body || '').trim();
  if (!comparable) {
    return '';
  }

  const conciseBehaviorTitle = buildConciseBehaviorTitle(comparable);
  if (conciseBehaviorTitle) {
    return conciseBehaviorTitle;
  }

  if (/\bno aggressive behavior(?:s)?\b/i.test(comparable) || /\bno aggression\b/i.test(comparable)) {
    return 'No aggressive behaviors';
  }

  if (/\bsensory break\b/i.test(comparable) || /\bcovered(?:\s+(?:her|his|their))?\s+ears\b/i.test(comparable)) {
    return 'Sensory break';
  }

  return '';
};

const resolvePreviewTitle = (entry = {}) => {
  const category = entry.parsedSourceCategory || entry.category || 'log';
  const title = String(entry.title || entry.titlePrefix || '').trim();
  const body = getPreviewBodyText(entry);
  const conciseBehaviorTitle = category === 'behavior'
    ? (resolveBehaviorPreviewTitle(body) || extractBodyDrivenTitle(body) || '')
    : '';

  if (category === 'behavior' && conciseBehaviorTitle) {
    const normalizedTitle = String(title || '').trim().toLowerCase();
    const normalizedBody = String(body || '').trim().toLowerCase();
    const normalizedConcise = String(conciseBehaviorTitle || '').trim().toLowerCase();
    const shouldCompressBehaviorTitle = (
      isGenericTitle(title, category)
      || isVerboseSentenceTitle(title)
      || normalizedTitle === normalizedBody
      || normalizedTitle.includes(' but ')
      || normalizedTitle.includes(' and ')
      || normalizedTitle.length > Math.max(36, normalizedConcise.length + 8)
    );

    if (shouldCompressBehaviorTitle) {
      return conciseBehaviorTitle;
    }
  }

  if (title && !isGenericTitle(title, category)) {
    return title;
  }

  if (category === 'food' && isGenericTitle(title, category)) {
    const foodSentence = extractBodyDrivenTitle(body);
    return foodSentence || titleCase(getFirstSentence(body)) || 'Food';
  }

  if (category === 'sleep' && isGenericTitle(title, category)) {
    if (/\bfell asleep\b/i.test(body)) return 'Fell asleep';
    if (/\bwoke up\b/i.test(body) || /\bwoke\b/i.test(body)) return 'Wake';
    if (/\bbedtime\b/i.test(body)) return 'Bedtime';
    return extractBodyDrivenTitle(body) || 'Sleep';
  }

  if (category === 'activity' && isGenericTitle(title, category)) {
    return extractBodyDrivenTitle(body) || 'Activity';
  }

  if (category === 'mood' && isGenericTitle(title, category)) {
    const moodTitle = resolveBehaviorPreviewTitle(body)
      || (body.match(/\b(happy|affectionate|tired|anxious|worried|excited|calm|frustrat(?:ed|ing)?|upset|sad|content|nervous|proud|mixed|overstimulated|relaxed)\b/i)?.[1] || '');
    return moodTitle ? titleCase(moodTitle) : extractBodyDrivenTitle(body) || 'Mood';
  }

  if (category === 'medication' && isGenericTitle(title, category)) {
    const medicationMatch = body.match(CARE_MEDICATION_REVIEW_NAME_PATTERN);
    const doseMatch = body.match(CARE_MEDICATION_DOSE_ANY_UNIT_PATTERN);
    if (medicationMatch && doseMatch) {
      return `${titleCase(medicationMatch[0])} ${doseMatch[0]}`;
    }
    if (medicationMatch) {
      return titleCase(medicationMatch[0]);
    }
    return extractBodyDrivenTitle(body) || 'Medication';
  }

  return extractBodyDrivenTitle(body) || title || entry.titlePrefix || '';
};

export { resolvePreviewTitle };

export const normalizeImportSaveError = (error) => {
  const rawMessage = String(error?.message || '').trim();
  const normalizedMessage = rawMessage.toLowerCase();

  if (
    error?.code === 'permission-denied' ||
    normalizedMessage.includes('missing or insufficient permissions') ||
    normalizedMessage.includes('permission-denied')
  ) {
    return 'You do not have permission to import logs for this child. Check that you are signed in with the right account and have access to this child.';
  }

  if (
    error?.code === 'deadline-exceeded' ||
    normalizedMessage.includes('gateway timeout') ||
    normalizedMessage.includes('504')
  ) {
    return 'That import took too long to save. Please split it into a smaller file and try again.';
  }

  return rawMessage || 'Something went wrong while saving the import.';
};

export const formatLocalDateInput = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseDateInputKeepingTime = (currentValue, nextDateValue) => {
  const currentDate = currentValue instanceof Date ? new Date(currentValue) : new Date(currentValue);
  const nextDate = typeof nextDateValue === 'string' ? nextDateValue : '';
  const parsed = nextDate ? new Date(`${nextDate}T00:00:00`) : null;

  if (!parsed || Number.isNaN(parsed.getTime())) {
    return currentDate instanceof Date && !Number.isNaN(currentDate.getTime()) ? currentDate : null;
  }

  const source = currentDate instanceof Date && !Number.isNaN(currentDate.getTime()) ? currentDate : new Date();
  parsed.setHours(source.getHours(), source.getMinutes(), source.getSeconds(), source.getMilliseconds());
  return parsed;
};

export const formatLocalTimeInput = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const parseTimeInputKeepingDate = (currentValue, nextTimeValue) => {
  const currentDate = currentValue instanceof Date ? new Date(currentValue) : new Date(currentValue);
  const nextTime = typeof nextTimeValue === 'string' ? nextTimeValue : '';
  const [hoursRaw, minutesRaw] = nextTime.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return currentDate instanceof Date && !Number.isNaN(currentDate.getTime()) ? currentDate : null;
  }

  const source = currentDate instanceof Date && !Number.isNaN(currentDate.getTime()) ? currentDate : new Date();
  source.setHours(hours, minutes, 0, 0);
  return source;
};

export const normalizePreviewRow = (entry, index) => {
  const timing = resolveImportPreviewTiming(entry);
  const timeBucket = entry?.timeBucket || entry?.metadata?.timeBucket || '';
  const inferredTimeSource = timeBucket ? 'inferred' : timing.timeSource;
  const bodyText = stripLeadingDateContext(
    entry.body || entry.note || entry.text || entry.sourceText || entry.content || '',
    entry
  );
  const dateLabel = normalizeDateContextLabel(
    entry?.dateLabel || entry?.metadata?.dateLabel || ''
  );
  const dateHeading = normalizeDateContextLabel(
    entry?.dateHeading || entry?.metadata?.dateHeading || dateLabel
  );
  const datePrecision = String(entry?.datePrecision || entry?.metadata?.datePrecision || '').trim();
  const previewTitle = resolvePreviewTitle(entry);
  const normalizedCategory = String(entry?.parsedSourceCategory || entry?.category || 'log').trim().toLowerCase();
  const behaviorPreviewTitle = normalizedCategory === 'behavior'
    ? (
      /\bcovered(?:\s+(?:her|his|their))?\s+ears\b/i.test(bodyText)
        ? 'Transition frustration'
        : (resolveBehaviorPreviewTitle(bodyText) || extractBodyDrivenTitle(bodyText) || previewTitle || 'Behavior')
    )
    : previewTitle;

  return {
    id: entry.id || `${Date.now()}-${index}`,
    date: timing.date,
    dateLabel,
    dateHeading,
    datePrecision,
    dateContext: entry?.dateContext || entry?.metadata?.dateContext || (dateLabel ? {
      label: dateLabel,
      precision: datePrecision,
      headingText: dateHeading,
    } : null),
    note: bodyText,
    body: bodyText,
    title: behaviorPreviewTitle,
    category: entry.parsedSourceCategory === 'note'
      ? 'note'
      : (entry.category || 'log'),
    importance: entry.importance === 'important' ? 'important' : 'normal',
    childName: entry.childName || null,
    include: true,
    reviewConfidence: entry.reviewConfidence || 'high',
    reviewConfidenceLabel: entry.reviewConfidenceLabel || 'Looks right',
    reviewConfidenceReasons: Array.isArray(entry.reviewConfidenceReasons) ? entry.reviewConfidenceReasons : [],
    timeSource: inferredTimeSource,
    timeInferred: Boolean(entry?.timeInferred || entry?.metadata?.timeInferred || timeBucket),
    timeInferenceReason: entry?.timeInferenceReason || entry?.metadata?.timeInferenceReason || timeBucket || '',
    localTime: timing.localTime,
    timeBucket,
    metadata: entry.metadata || null,
    capturedAt: entry.capturedAt || entry?.metadata?.capturedAt || '',
    sourceText: entry.sourceText || entry.originalText || entry.text || entry.note || entry.body || entry.content || '',
    originalText: entry.originalText || entry.sourceText || entry.text || entry.note || entry.body || entry.content || '',
    text: entry.text || entry.note || entry.body || entry.sourceText || entry.content || '',
    content: entry.content || entry.text || entry.note || entry.body || entry.sourceText || '',
    severity: entry.severity || '',
    severityLabel: entry.severityLabel || '',
    triggerSummary: entry.triggerSummary || '',
    incidentCategoryIcon: entry.incidentCategoryIcon || '',
    categoryIcon: entry.categoryIcon || '',
    medicationName: entry.medicationName || '',
    medicationDose: entry.medicationDose || '',
    medicationUnit: entry.medicationUnit || '',
    activityThemeLabel: entry.activityThemeLabel || '',
    activityType: entry.activityType || '',
    activityTypes: Array.isArray(entry.activityTypes) ? entry.activityTypes.join(', ') : (entry.activityTypes || ''),
    parsedObservation: Boolean(entry.parsedObservation),
    parsedSourceCategory: entry.parsedSourceCategory || '',
    parsedSourceConfidence: entry.parsedSourceConfidence || '',
    source: entry.source || '',
    mode: entry.mode || '',
  };
};

export const formatPreviewTime = (value, timeSource = 'explicit', timeBucket = '', timeInferred = false, timeInferenceReason = '') => {
  if (timeSource === 'explicit') {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return getTimeUnclearLabel();
    }

    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  if (timeSource === 'missing') {
    return getTimeUnclearLabel();
  }

  if (timeInferred || timeBucket || isApproximateTimeSource(timeSource)) {
    return getTimeInferenceLabel(timeBucket, timeInferenceReason) || getTimeUnclearLabel();
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return getTimeUnclearLabel();
  }

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const getCategoryLabel = (category = '') => {
  if (category === 'note') {
    return 'Note';
  }

  if (category === 'daily' || category === 'log') {
    return 'Daily Log';
  }

  const match = CATEGORY_OPTIONS.find((option) => option.value === category);
  if (match?.label) {
    return match.label;
  }

  if (!category) {
    return 'Daily Log';
  }

  return category
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const getCategoryGlyph = (entry = {}) => {
  const normalizedCategory = entry?.category === 'daily' ? 'log' : (entry?.category === 'note' ? 'log' : entry?.category);
  if (normalizedCategory === 'behavior') {
    const logType = getLogTypeByCategory('behavior');
    return logType?.iconComponent ? null : (logType?.icon || '•');
  }

  if (entry?.incidentCategoryIcon) {
    return entry.incidentCategoryIcon;
  }

  if (entry?.categoryIcon) {
    return entry.categoryIcon;
  }

  const logType = getLogTypeByCategory(normalizedCategory || 'log');
  return logType?.iconComponent ? null : (logType?.icon || '•');
};

export const renderCategoryIcon = (entry = {}) => {
  const normalizedCategory = entry?.category === 'daily' ? 'log' : (entry?.category === 'note' ? 'log' : entry?.category);
  const categoryMeta = getLogTypeByCategory(normalizedCategory || 'log');

  if (categoryMeta?.iconComponent) {
    const CategoryIconComponent = categoryMeta.iconComponent;
    return (
      <Box
        component="span"
        sx={{
          width: 16,
          height: 16,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto',
          lineHeight: 1,
        }}
      >
        <CategoryIconComponent sx={{ fontSize: '0.95rem' }} />
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={{
        width: 16,
        height: 16,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 auto',
        fontSize: '0.88rem',
        lineHeight: 1,
      }}
    >
      {getCategoryGlyph(entry)}
    </Box>
  );
};

export const buildSkippedSummaryRow = ({
  row = {},
  reason = 'firestore_write_error',
  explanation = '',
  severity = 'hard',
  source = 'save',
  title = '',
} = {}) => {
  const preview = String(row?.note || row?.text || '').trim();
  const dateValue = row?.date instanceof Date
    ? row.date
    : (row?.date ? new Date(row.date) : null);
  const safeTime = dateValue && !Number.isNaN(dateValue.getTime())
    ? dateValue.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : '';

  return {
    id: `${source}:${reason}:${title || preview || safeTime || Math.random().toString(36).slice(2)}`,
    preview: preview.length > 120 ? `${preview.slice(0, 117)}…` : preview,
    reason,
    explanation,
    severity,
    source,
    category: row?.category || 'log',
    title: title || getCategoryLabel(row?.category || 'log'),
    time: safeTime,
    sourceRow: row,
  };
};

export const getSkippedReasonLabel = (skippedRow = {}) => {
  if (['firestore_write_error', 'invalid_payload'].includes(skippedRow.reason)) {
    return "Couldn't save this entry";
  }

  if (skippedRow.reason === 'duplicate_existing' || skippedRow.reason === 'duplicate_in_batch') {
    return 'Skipped duplicate entry';
  }

  return String(skippedRow.reason || 'Skipped entry').replace(/_/g, ' ');
};

export const getSkippedReasonExplanation = (skippedRow = {}) => {
  if (['firestore_write_error', 'invalid_payload'].includes(skippedRow.reason)) {
    return skippedRow.explanation || 'We couldn’t save this entry. You can review or edit it and try again.';
  }

  if (skippedRow.reason === 'duplicate_existing') {
    return skippedRow.explanation || 'This entry matched an existing log and was turned off before saving.';
  }

  if (skippedRow.reason === 'duplicate_in_batch') {
    return skippedRow.explanation || 'This entry matched another row in this batch and was removed before saving.';
  }

  return skippedRow.explanation || 'This row was skipped.';
};

export const dedupeSkippedSummaryRows = (rows = []) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = [
      row.source || '',
      row.reason || '',
      row.preview || '',
      row.time || '',
      row.title || '',
    ].join('|');
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};
